import { FormControl, Autocomplete, TextField, createFilterOptions } from '@mui/material';
import { EMasterPropertyCodes } from 'data/Constants';

import type { IAutocompleteProps, IOption } from '../../../types';

/**
 * Returns the display label for an autocomplete option based on the property code.
 * @param {string} code - The property code used to determine the label format.
 * @param option - The option object containing `code` and `name` fields.
 * @returns {string} The formatted label string.
 */
const formatOptionLabel = (code: string, option: IOption): string => {
  if (code === EMasterPropertyCodes.RCID) {
    return `${option.code} (${option.name})`;
  } else {
    return `${option.name} (${option.code})`;
  }
};

/**
 * This component renders Autocomplete component to select values
 * @param {IAutocompleteProps} props AutoComplete Props
 * @returns {JSX.Element} a  Autocomplete component.
 */
const AutocompleteInput = (props: IAutocompleteProps) => {
  const handleInputChange = (newVal) => {
    const val = newVal ? JSON.stringify([newVal.code]) : null;
    props.code ? props.handleChange(props.code, val) : props.handleChange(newVal);
  };

  const isValueMismatch = (props.value as { code: string })?.code === 'NA';

  return (
    <FormControl fullWidth>
      <Autocomplete
        id={props.code ?? 'autocomplete'}
        size={props.size}
        options={props.data}
        onChange={(_, newVal) => handleInputChange(newVal)}
        getOptionLabel={(option) => {
          if (props.showCode) {
            return formatOptionLabel(props.code, option);
          }
          if (props.showSequenceCode) return `${option.name} (${option.sequence})`;
          return option.name;
        }}
        filterOptions={createFilterOptions({ trim: true })} // Create a custom filter that trims whitespace
        disableClearable={props.disableClear}
        value={props?.value}
        disabled={props.disabled}
        data-testid={props.id}
        className={isValueMismatch ? 'autocomplete-value-mismatch' : undefined}
        renderInput={(params) => (
          <TextField {...params} label={props.label} error={!!isValueMismatch} />
        )}
      />
    </FormControl>
  );
};

export default AutocompleteInput;
